import * as THREE from "three";
import { windUniforms } from "./decorWind";

/** Part visible des vagues, de 0 (vu de loin, eau unie) à 1 ; mise à jour à chaque image. */
export const waterUniforms = { uWaterDetail: { value: 1 } };

/** Les vagues apparaissent entre ces deux zooms. */
export const WATER_DETAIL_ZOOMS = [17.5, 18.5] as const;

export function waterDetailShare(zoom: number): number {
  const [from, to] = WATER_DETAIL_ZOOMS;
  return Math.min(1, Math.max(0, (zoom - from) / (to - from)));
}

const VERTEX_HEAD = /* glsl */ `
  varying vec2 vWaterXZ;
  varying vec3 vWaterAxisX;
  varying vec3 vWaterAxisZ;
`;

const VERTEX_BODY = /* glsl */ `
  #include <begin_vertex>
  vWaterXZ = position.xz;
  vWaterAxisX = normalize((modelViewMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
  vWaterAxisZ = normalize((modelViewMatrix * vec4(0.0, 0.0, 1.0, 0.0)).xyz);
`;

const FRAGMENT_HEAD = /* glsl */ `
  uniform float uWindTime;
  uniform float uWaterDetail;
  varying vec2 vWaterXZ;
  varying vec3 vWaterAxisX;
  varying vec3 vWaterAxisZ;

  vec3 waterWaves(vec2 p, float t) {
    vec2 d1 = vec2(0.8, 0.6), d2 = vec2(-0.5, 0.85), d3 = vec2(0.95, -0.3);
    float a1 = dot(p, d1) * 2.1 + t * 1.3;
    float a2 = dot(p, d2) * 3.3 - t * 1.7;
    float a3 = dot(p, d3) * 5.2 + t * 2.4;
    float height = 0.5 * sin(a1) + 0.3 * sin(a2) + 0.2 * sin(a3);
    vec2 slope = d1 * 1.05 * cos(a1) + d2 * 0.99 * cos(a2) + d3 * 1.04 * cos(a3);
    return vec3(slope, height);
  }

  // Trois séries de filets à 60° l'une de l'autre, sur un plan ondulé : ni grille ni lignes droites.
  float waterRidges(vec2 p, float t, float height) {
    vec2 q = p + 0.55 * vec2(sin(p.y * 0.73 + t * 0.41), sin(p.x * 0.61 - t * 0.33));
    float a = abs(sin(dot(q, vec2(1.0, 0.0)) * 2.3 + t * 0.9 + 1.1 * height));
    float b = abs(sin(dot(q, vec2(-0.5, 0.866)) * 2.7 - t * 0.7 + 0.8 * height));
    float c = abs(sin(dot(q, vec2(-0.5, -0.866)) * 3.1 + t * 0.6 - 0.9 * height));
    return pow(1.0 - a * b * c, 14.0);
  }
`;

const FRAGMENT_COLOR = /* glsl */ `
  #include <color_fragment>
  vec3 waterWave = waterWaves(vWaterXZ, uWindTime);
  float waterCrest = waterRidges(vWaterXZ, uWindTime, waterWave.z);
  diffuseColor.rgb = mix(diffuseColor.rgb * (1.0 + 0.05 * waterWave.z * uWaterDetail), vec3(0.85, 0.95, 1.0), 0.18 * waterCrest * uWaterDetail);
`;

const FRAGMENT_NORMAL = /* glsl */ `
  #include <normal_fragment_maps>
  normal = normalize(normal - 0.08 * uWaterDetail * (vWaterAxisX * waterWave.x + vWaterAxisZ * waterWave.y));
`;

/** Des vagues calculées par la carte graphique, sur l'horloge du décor : aucun calcul côté processeur. */
export function animateWater(material: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uWindTime = windUniforms.uWindTime;
    shader.uniforms.uWaterDetail = waterUniforms.uWaterDetail;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${VERTEX_HEAD}`)
      .replace("#include <begin_vertex>", VERTEX_BODY);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${FRAGMENT_HEAD}`)
      .replace("#include <color_fragment>", FRAGMENT_COLOR)
      .replace("#include <normal_fragment_maps>", FRAGMENT_NORMAL);
  };
  material.customProgramCacheKey = () => "water-waves";
  return material;
}
